import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { CreateParty, Party, UpdateParty } from '@dashboard/contracts';
import { apiRequest } from '../client';
import { defaultQueryOptions } from './shared';

/**
 * The organization register (docs/organizations-design.md).
 *
 * The hooks keep the code name -- `useParties`, `Party` -- because that is what
 * the contracts call the row; the API path and the screen say organization.
 */

export const organizationKeys = {
  all: ['organizations'] as const,
};

export function useParties(enabled = true) {
  return useQuery({
    ...defaultQueryOptions,
    queryKey: organizationKeys.all,
    queryFn: ({ signal }) => apiRequest<Party[]>('/api/v1/organizations', { signal }),
    enabled,
  });
}

/**
 * An organization's name appears inside every contract row, every cost entry
 * and every account's membership, so a rename that refreshed only the register
 * would leave the old name on screen where it is read.
 */
function invalidateOrganizations(client: QueryClient) {
  void client.invalidateQueries({ queryKey: organizationKeys.all });
  void client.invalidateQueries({ queryKey: ['contracts'] });
  void client.invalidateQueries({ queryKey: ['costs'] });
  void client.invalidateQueries({ queryKey: ['users'] });
}

export function useCreateParty() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateParty) =>
      apiRequest<Party>('/api/v1/organizations', { method: 'POST', body }),
    onSuccess: () => invalidateOrganizations(client),
  });
}

export function useUpdateParty() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: UpdateParty & { id: string }) =>
      apiRequest<Party>(`/api/v1/organizations/${id}`, { method: 'PATCH', body }),
    onSuccess: () => invalidateOrganizations(client),
  });
}

export function useDeleteParty() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<void>(`/api/v1/organizations/${id}`, { method: 'DELETE' }),
    onSuccess: () => invalidateOrganizations(client),
  });
}
